import type { JSX } from "react";
import type { ValueOrI18nKeyProp } from "../../util/type";
import type { TranslationService } from "../translation/translation-service";
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, styled } from "@mui/material";
import { Trans } from "react-i18next";
import { Form } from "../../components/atoms/form";
import { ContentWidth } from "../../components/atoms/content-width";
import { Prompt } from "../../prompt/prompt";
import { useServices } from "../services-context";


export type ConfirmationPromptConfig = (
	& Partial<ValueOrI18nKeyProp<"title">>
	& ValueOrI18nKeyProp<"body">
	& Partial<ValueOrI18nKeyProp<"checkbox", string | boolean>>
	& Partial<ValueOrI18nKeyProp<"accept">>
	& Partial<ValueOrI18nKeyProp<"reject">>
	& {
		dangerous?: boolean;
	}
);

type CheckboxInfo = {
	enabled: false;
} | {
	enabled: true;
	label: string;
};

const getCheckboxInfo = (config: ConfirmationPromptConfig, translation: TranslationService): CheckboxInfo => {
	if(config.checkbox === true) {
		return {
			enabled: true,
			label: translation.translate("common:confirm"),
		};
	}

	const resolved = translation.resolveValueOrI18nKey(config.checkbox, config.checkboxKey, config.checkboxValues);
	if(!resolved) {
		return {
			enabled: false,
		};
	}

	return {
		enabled: true,
		label: resolved as string,
	};
};

export class ConfirmationPrompt extends Prompt<boolean> {
  override view = (): JSX.Element => <ConfirmationPromptView prompt={ this }/>;

	readonly config: ConfirmationPromptConfig;

	constructor(config: ConfirmationPromptConfig) {
		super();
		this.config = config;
	}
}

const BodyText = styled(Stack)`
	p {
		margin: 0;
	}
`;

type ConfirmationPromptViewProps = { prompt: ConfirmationPrompt };
const ConfirmationPromptView = ({ prompt }: ConfirmationPromptViewProps): JSX.Element => {
	const { config } = prompt;
	const { translation } = useServices();
	const title = translation.resolveValueOrI18nKey(config.title, config.titleKey, config.titleValues) as string | JSX.Element | undefined;

	const checkbox = getCheckboxInfo(config, translation);

	const content = (
		<Box sx={ theme => ({ maxWidth: theme.layout.contentNarrowMaxWidth }) }>
			{ title && <DialogTitle>{ title }</DialogTitle> }
			<DialogContent>
				<Stack spacing={ 2 }>
					{ config.body }
					{ !config.body && (
						<BodyText>
							<Trans
								i18nKey={ config.bodyKey }
								values={ config.bodyValues }
							/>
						</BodyText>
					) }

					{ checkbox.enabled && (
						<FormControlLabel
							control={ <Checkbox required/> }
							label={ checkbox.label }
						/>
					) }
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button
					variant="text"
					onClick={ () => prompt.submit(false) }
					type={ "button" }
				>
					{ translation.resolveValueOrI18nKey(config.reject, config.rejectKey, config.rejectValues) || translation.translate("common:cancel") }
				</Button>
				<Button
					variant="text"
					type={ checkbox.enabled ? "submit" : "button" }
					onClick={ checkbox.enabled ? undefined : () => prompt.submit(true) }
					color={ prompt.config.dangerous ? "error" : undefined }
				>
					{ translation.resolveValueOrI18nKey(config.accept, config.acceptKey, config.acceptValues) || translation.translate("common:ok") }
				</Button>
			</DialogActions>
		</Box>
	);

	if(checkbox.enabled) {
		return (
			<Form onSubmit={ () => prompt.submit(true) }>
				{ content }
			</Form>
		);
	}

	return (
		<ContentWidth narrow>
			{ content }
		</ContentWidth>
	);
};
